import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  Select,
} from "@mui/material";
import React from "react";
import { COLORS } from "../assets/theme";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import CustomTextField from "../components/CustomTextField";
import CustomSelect from "../components/CustomSelect";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import { countries } from "../assets/utils/countries";
import {
  createSupplier,
  fetchSuppliers,
  getSupplier,
  updateSupplier,
  fetchAllSupplierExpenses,
  fetchSupplierExpenses,
  getSupplierExpenseById,
} from "../services/SuppliersService";
import CustomButton from "../components/CustomButton";
import { useParams } from "react-router-dom";
import { useTabParam } from "../hooks/useTabParam";
import { toast } from "react-toastify";
import { ArrowBackIosNew, ArrowForwardIos, Visibility } from "@mui/icons-material";
import CustomDateField from "../components/CustomDateField";
import CustomDialog from "../components/CustomDialog";
import ViewPurchaseAndPrint from "../components/ViewPurchaseAndPrint";

interface SupplierRow {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  country: string;
  companyPhone?: string;
  supplierName?: string;
  supplierPhone?: string;
  additionalPhone?: string;
  status: string;
  gender?: string;
  dob?: string;
  banner?: string | null;
}

const DEFAULT_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <circle cx="100" cy="100" r="100" fill="#c4c4c4"/>
      <circle cx="100" cy="80" r="38" fill="#f2f2f2"/>
      <path d="M40 170c0-33 27-60 60-60s60 27 60 60" fill="#f2f2f2"/>
    </svg>`
  );

const Purchases: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;

  if (!businessId) {
    throw new Error("Purchases page requires businessId in route");
  }

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // Supplier Details/History need a supplier picked first, so only the first
  // two tabs are restored from the URL.
  const { tab, setTab, panelRef: tabPanelRef } = useTabParam(
    ["suppliers", "purchases", "supplier-details", "supplier-history"],
    { persist: (index) => index < 2 }
  );
  const [loadingSuppliers, setLoadingSuppliers] = React.useState(true);
  const [suppliersError, setSuppliersError] = React.useState<string | null>(
    null
  );
  const [suppliers, setSuppliers] = React.useState<SupplierRow[]>([]);
  const [search, setSearch] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = React.useState<
    string | null
  >(null);
  const [supplierDetails, setSupplierDetails] = React.useState<any | null>(null);
  const [supplierDetailsLoading, setSupplierDetailsLoading] =
    React.useState(false);
  const [supplierDetailsError, setSupplierDetailsError] = React.useState<
    string | null
  >(null);
  const [purchasesPage, setPurchasesPage] = React.useState(1);
  const [purchasesSize, setPurchasesSize] = React.useState(10);
  const [purchasesTotal, setPurchasesTotal] = React.useState(0);
  const [purchasesTotalAmount, setPurchasesTotalAmount] = React.useState(0);
  const [purchasesLoading, setPurchasesLoading] = React.useState(false);
  const [purchasesError, setPurchasesError] = React.useState<string | null>(null);
  const [purchasesExpenses, setPurchasesExpenses] = React.useState<any[]>([]);
  const [supplierHistoryFrom, setSupplierHistoryFrom] = React.useState<string | null>(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return formatDateInput(weekAgo);
  });
  const [supplierHistoryTo, setSupplierHistoryTo] = React.useState<string | null>(() => {
    return formatDateInput(new Date());
  });
  const [supplierHistoryPage, setSupplierHistoryPage] = React.useState(1);
  const [supplierHistorySize, setSupplierHistorySize] = React.useState(10);
  const [supplierHistoryTotal, setSupplierHistoryTotal] = React.useState(0);
  const [supplierHistoryTotalAmount, setSupplierHistoryTotalAmount] = React.useState(0);
  const [supplierHistoryLoading, setSupplierHistoryLoading] = React.useState(false);
  const [supplierHistoryError, setSupplierHistoryError] = React.useState<string | null>(null);
  const [supplierHistoryExpenses, setSupplierHistoryExpenses] = React.useState<any[]>([]);
  const [viewExpenseOpen, setViewExpenseOpen] = React.useState(false);
  const [viewExpenseLoading, setViewExpenseLoading] = React.useState(false);
  const [viewExpenseError, setViewExpenseError] = React.useState<string | null>(null);
  const [viewExpenseData, setViewExpenseData] = React.useState<any | null>(null);

  const purchasesUnpaidSummary = React.useMemo(() => {
    let count = 0;
    let amount = 0;
    purchasesExpenses.forEach((exp) => {
      const isPaid =
        exp?.paid === true || String(exp?.status || "").toUpperCase() === "PAID";
      if (isPaid) return;
      count += 1;
      amount += Number(exp?.amount) || 0;
    });
    return { count, amount };
  }, [purchasesExpenses]);

  const supplierHistoryUnpaidSummary = React.useMemo(() => {
    let count = 0;
    let amount = 0;
    supplierHistoryExpenses.forEach((exp) => {
      const isPaid =
        exp?.paid === true || String(exp?.status || "").toUpperCase() === "PAID";
      if (isPaid) return;
      count += 1;
      amount += Number(exp?.amount) || 0;
    });
    return { count, amount };
  }, [supplierHistoryExpenses]);

  const openExpenseView = React.useCallback(
    async (expense: any, fallbackSupplierId?: string | null) => {
      const expenseId = expense?.id;
      const supplierId =
        expense?.metadata?.supplierOrgUserId ||
        expense?.metadata?.supplier_org_user_id ||
        fallbackSupplierId;

      if (!expenseId || !supplierId) {
        toast.error("Missing supplier or expense id.");
        return;
      }

      setViewExpenseOpen(true);
      setViewExpenseLoading(true);
      setViewExpenseError(null);
      setViewExpenseData(null);

      try {
        const res = await getSupplierExpenseById(String(supplierId), Number(expenseId));
        const payload = (res as any)?.data ?? res;
        const expense = (payload as any)?.expense ?? payload;
        const items = (payload as any)?.items ?? [];
        setViewExpenseData({ expense, items });
      } catch (err) {
        console.error("Failed to load supplier expense:", err);
        setViewExpenseError("Failed to load purchase details.");
      } finally {
        setViewExpenseLoading(false);
      }
    },
    []
  );
  const [isEditingSupplier, setIsEditingSupplier] = React.useState(false);
  const [savingSupplier, setSavingSupplier] = React.useState(false);
  const [supplierName, setSupplierName] = React.useState("");
  const [supplierEmail, setSupplierEmail] = React.useState("");
  const [supplierPhone, setSupplierPhone] = React.useState("");
  const [supplierCountry, setSupplierCountry] = React.useState("");
  const [supplierContactName, setSupplierContactName] = React.useState("");
  const [supplierContactPhone, setSupplierContactPhone] = React.useState("");
  const [supplierContactCountry, setSupplierContactCountry] = React.useState("");
  const [supplierAdditionalPhone, setSupplierAdditionalPhone] = React.useState("");
  const [supplierAdditionalCountry, setSupplierAdditionalCountry] = React.useState("");
  const [supplierStatus, setSupplierStatus] = React.useState("");
  const [supplierBanner, setSupplierBanner] = React.useState<string | null>(null);
  const [supplierBannerFile, setSupplierBannerFile] = React.useState<File | null>(null);
  const [supplierInitialData, setSupplierInitialData] = React.useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    status: "",
    contactName: "",
    contactPhone: "",
    contactCountry: "",
    additionalPhone: "",
    additionalCountry: "",
  });
  const supplierBannerInputRef = React.useRef<HTMLInputElement | null>(null);
  const supplierPreviewUrlRef = React.useRef<string | null>(null);

  function formatDateInput(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const [purchasesFrom, setPurchasesFrom] = React.useState<string | null>(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return formatDateInput(weekAgo);
  });

  const [purchasesTo, setPurchasesTo] = React.useState<string | null>(() => {
    return formatDateInput(new Date());
  });

  const formatDateDMY = (value?: string | null) => {
    if (!value) return "—";
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-");
      return `${d}-${m}-${y}`;
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const d = String(parsed.getDate()).padStart(2, "0");
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const y = String(parsed.getFullYear());
    return `${d}-${m}-${y}`;
  };

  React.useEffect(() => {
    if (!supplierDetails) {
      setSupplierName("");
      setSupplierEmail("");
      setSupplierPhone("");
      setSupplierCountry("");
      setSupplierStatus("");
      setSupplierBanner(null);
      setSupplierBannerFile(null);
      setSupplierInitialData({
        name: "",
        email: "",
        phone: "",
        country: "",
        status: "",
        contactName: "",
        contactPhone: "",
        contactCountry: "",
        additionalPhone: "",
        additionalCountry: "",
      });
      setIsEditingSupplier(false);
      return;
    }
    setSupplierName(
      supplierDetails?.company_name ||
        supplierDetails?.companyName ||
        supplierDetails?.full_name ||
        supplierDetails?.fullName ||
        ""
    );
    setSupplierEmail(
      supplierDetails?.supplier_name ||
        supplierDetails?.supplierName ||
        supplierDetails?.user?.email ||
        supplierDetails?.email ||
        ""
    );
    setSupplierPhone(
      supplierDetails?.company_phone || supplierDetails?.companyPhone || supplierDetails?.phone || ""
    );
    setSupplierCountry(
      supplierDetails?.company_country || supplierDetails?.companyCountry || supplierDetails?.country || ""
    );
    setSupplierContactName(
      supplierDetails?.supplier_name || supplierDetails?.supplierName || ""
    );
    setSupplierContactPhone(
      supplierDetails?.supplier_phone || supplierDetails?.supplierPhone || ""
    );
    setSupplierContactCountry(
      supplierDetails?.supplier_country || supplierDetails?.supplierCountry || ""
    );
    setSupplierAdditionalPhone(
      supplierDetails?.additional_phone || supplierDetails?.additionalPhone || ""
    );
    setSupplierAdditionalCountry(
      supplierDetails?.additional_country || supplierDetails?.additionalCountry || ""
    );
    const normalizedStatus =
      supplierDetails?.active === false ? "Inactive" : "Active";
    setSupplierStatus(normalizedStatus);
    setSupplierBanner(
      supplierDetails?.banner && String(supplierDetails.banner).trim()
        ? supplierDetails.banner
        : null
    );
    setSupplierBannerFile(null);
    setSupplierInitialData({
      name:
        supplierDetails?.company_name ||
        supplierDetails?.companyName ||
        supplierDetails?.full_name ||
        supplierDetails?.fullName ||
        "",
      email:
        supplierDetails?.supplier_name ||
        supplierDetails?.supplierName ||
        supplierDetails?.user?.email ||
        supplierDetails?.email ||
        "",
      phone:
        supplierDetails?.company_phone ||
        supplierDetails?.companyPhone ||
        supplierDetails?.phone ||
        "",
      country:
        supplierDetails?.company_country ||
        supplierDetails?.companyCountry ||
        supplierDetails?.country ||
        "",
      status: normalizedStatus,
      contactName:
        supplierDetails?.supplier_name || supplierDetails?.supplierName || "",
      contactPhone:
        supplierDetails?.supplier_phone || supplierDetails?.supplierPhone || "",
      contactCountry:
        supplierDetails?.supplier_country || supplierDetails?.supplierCountry || "",
      additionalPhone:
        supplierDetails?.additional_phone ||
        supplierDetails?.additionalPhone ||
        "",
      additionalCountry:
        supplierDetails?.additional_country ||
        supplierDetails?.additionalCountry ||
        "",
    });
    setIsEditingSupplier(false);
  }, [supplierDetails]);

  const handleTabChange = async (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  React.useEffect(() => {
    setLoading(false);
    setError(null);
  }, []);

  React.useEffect(() => {
    if (supplierBannerFile) {
      if (supplierPreviewUrlRef.current) {
        URL.revokeObjectURL(supplierPreviewUrlRef.current);
      }
      const preview = URL.createObjectURL(supplierBannerFile);
      supplierPreviewUrlRef.current = preview;
      setSupplierBanner(preview);
    }
    return () => {
      if (supplierPreviewUrlRef.current) {
        URL.revokeObjectURL(supplierPreviewUrlRef.current);
        supplierPreviewUrlRef.current = null;
      }
    };
  }, [supplierBannerFile]);

  const loadSuppliers = React.useCallback(async () => {
    setLoadingSuppliers(true);
    setSuppliersError(null);
    try {
      const res = await fetchSuppliers();
      const data = (res as any)?.data ?? res;
      const list = Array.isArray(data) ? data : data?.suppliers || [];

      const rows: SupplierRow[] = (Array.isArray(list) ? list : []).map(
        (s: any) => ({
          id: String(s?.id ?? ""),
          fullName:
            s?.company_name ||
            s?.companyName ||
            s?.supplier_name ||
            s?.supplierName ||
            s?.full_name ||
            s?.fullName ||
            s?.name ||
            "",
          email:
            s?.supplier_name ||
            s?.supplierName ||
            s?.user?.email ||
            s?.email ||
            "",
          phone: s?.supplier_phone || s?.company_phone || s?.phone || s?.mobile || "",
          companyPhone: s?.company_phone || s?.companyPhone || "",
          supplierName: s?.supplier_name || s?.supplierName || "",
          supplierPhone: s?.supplier_phone || s?.supplierPhone || "",
          additionalPhone: s?.additional_phone || s?.additionalPhone || "",
          country: s?.supplier_country || s?.company_country || s?.country || "",
          status: s?.active === false ? "Inactive" : "Active",
          banner:
            s?.banner && String(s.banner).trim() ? String(s.banner) : null,
        })
      );

      setSuppliers(rows);
    } catch (err) {
      console.error("Failed to fetch suppliers:", err);
      setSuppliers([]);
      setSuppliersError("Failed to load suppliers. Please try again.");
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  React.useEffect(() => {
    if (tab !== 0) return;
    loadSuppliers();
  }, [tab, loadSuppliers]);

  React.useEffect(() => {
    if (tab !== 1) return;

    const loadPurchases = async () => {
      setPurchasesLoading(true);
      setPurchasesError(null);
      try {
        const params = {
          ...(purchasesFrom ? { from: purchasesFrom } : {}),
          ...(purchasesTo ? { to: purchasesTo } : {}),
          page: Math.max(purchasesPage - 1, 0),
          size: purchasesSize,
        };
        const res = await fetchAllSupplierExpenses(params);
        const payload = (res as any)?.data ?? res;
        const expenses =
          payload?.expenses ??
          payload?.data?.expenses ??
          payload?.items ??
          payload?.results ??
          [];
        setPurchasesExpenses(Array.isArray(expenses) ? expenses : []);
        console.log("fetchAllSupplierExpenses response:", res);

        const total =
          payload?.totalCount ??
          payload?.total ??
          payload?.totalElements ??
          payload?.count ??
          payload?.pagination?.totalElements;
        if (typeof total === "number") {
          setPurchasesTotal(total);
        }

        const totalAmount =
          payload?.totalAmount ??
          payload?.total_amount ??
          payload?.sum ??
          payload?.amountTotal;
        if (typeof totalAmount === "number") {
          setPurchasesTotalAmount(totalAmount);
        }
      } catch (err) {
        console.error("Failed to fetch supplier purchases:", err);
        setPurchasesExpenses([]);
        setPurchasesError("Failed to load purchases. Please try again.");
      } finally {
        setPurchasesLoading(false);
      }
    };

    loadPurchases();
  }, [tab, purchasesFrom, purchasesTo, purchasesPage, purchasesSize]);

  React.useEffect(() => {
    if (tab === 2 || tab === 3) return;
    setSelectedSupplierId(null);
    setSupplierDetails(null);
    setSupplierDetailsError(null);
  }, [tab]);

  React.useEffect(() => {
    if (tab !== 3 || !selectedSupplierId) return;

    const loadSupplierHistory = async () => {
      setSupplierHistoryLoading(true);
      setSupplierHistoryError(null);
      try {
        const params = {
          ...(supplierHistoryFrom ? { from: supplierHistoryFrom } : {}),
          ...(supplierHistoryTo ? { to: supplierHistoryTo } : {}),
          page: Math.max(supplierHistoryPage - 1, 0),
          size: supplierHistorySize,
        };
        const res = await fetchSupplierExpenses(selectedSupplierId, params);
        console.log("fetchSupplierExpenses response:", res);

        const payload = (res as any)?.data ?? res;
        const expenses =
          payload?.expenses ??
          payload?.data?.expenses ??
          payload?.items ??
          payload?.results ??
          [];
        setSupplierHistoryExpenses(Array.isArray(expenses) ? expenses : []);
        const total =
          payload?.totalCount ??
          payload?.total ??
          payload?.totalElements ??
          payload?.count ??
          payload?.pagination?.totalElements;
        if (typeof total === "number") {
          setSupplierHistoryTotal(total);
        }

        const totalAmount =
          payload?.totalAmount ??
          payload?.total_amount ??
          payload?.sum ??
          payload?.amountTotal;
        if (typeof totalAmount === "number") {
          setSupplierHistoryTotalAmount(totalAmount);
        }
      } catch (err) {
        console.error("Failed to fetch supplier history:", err);
        setSupplierHistoryExpenses([]);
        setSupplierHistoryError("Failed to load supplier history. Please try again.");
      } finally {
        setSupplierHistoryLoading(false);
      }
    };

    loadSupplierHistory();
  }, [
    tab,
    selectedSupplierId,
    supplierHistoryFrom,
    supplierHistoryTo,
    supplierHistoryPage,
    supplierHistorySize,
  ]);

  const filteredSuppliers = React.useMemo(() => {
    const q = String(search || "").toLowerCase().trim();
    if (!q) return suppliers;
    return suppliers.filter((s) => {
      const haystack = [
        s.fullName,
        s.email,
        s.phone,
        s.country,
        s.gender,
        s.status,
        s.dob,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [search, suppliers]);

  const loadSupplierDetails = React.useCallback(async (id: string) => {
    setSupplierDetailsLoading(true);
    setSupplierDetailsError(null);
    try {
      const supplier: any = await getSupplier(id);
      setSupplierDetails(supplier);
    } catch (err) {
      console.error("Failed to load supplier details:", err);
      setSupplierDetails(null);
      setSupplierDetailsError("Failed to load supplier details.");
    } finally {
      setSupplierDetailsLoading(false);
    }
  }, []);

  const handleSaveSupplier = async () => {
    if (!selectedSupplierId) return;

    const trimmedName = supplierName.trim();
    const trimmedCountry = supplierCountry.trim();
    const trimmedPhone = supplierPhone.trim();

    try {
      setSavingSupplier(true);
      const payload: any = {};

      const assign = (key: string, value: any) => {
        if (value !== undefined && value !== null) payload[key] = value;
      };

      if (trimmedName !== supplierInitialData.name)
        assign("company_name", trimmedName);
      if (trimmedPhone !== supplierInitialData.phone)
        assign("company_phone", trimmedPhone);
      if (trimmedCountry !== supplierInitialData.country)
        assign("company_country", trimmedCountry);
      if (supplierContactName !== supplierInitialData.contactName)
        assign("supplier_name", supplierContactName);
      if (supplierContactPhone !== supplierInitialData.contactPhone)
        assign("supplier_phone", supplierContactPhone);
      if (supplierContactCountry !== supplierInitialData.contactCountry)
        assign("supplier_country", supplierContactCountry);
      if (supplierAdditionalPhone !== supplierInitialData.additionalPhone)
        assign("additional_phone", supplierAdditionalPhone);
      if (supplierAdditionalCountry !== supplierInitialData.additionalCountry)
        assign("additional_country", supplierAdditionalCountry);

      if (supplierStatus !== supplierInitialData.status)
        assign("active", supplierStatus?.toLowerCase() === "active");

      const hasChanges =
        Object.keys(payload).length > 0 || Boolean(supplierBannerFile);
      if (!hasChanges) {
        toast.info("No changes to save");
        setIsEditingSupplier(false);
        return;
      }

      await updateSupplier(
        selectedSupplierId,
        payload,
        supplierBannerFile || undefined
      );
      toast.success("Supplier updated successfully");

      setSupplierInitialData({
        name: trimmedName,
        email: supplierContactName,
        phone: trimmedPhone,
        country: trimmedCountry,
        status: supplierStatus,
        contactName: supplierContactName,
        contactPhone: supplierContactPhone,
        contactCountry: supplierContactCountry,
        additionalPhone: supplierAdditionalPhone,
        additionalCountry: supplierAdditionalCountry,
      });

      setSupplierDetails((prev: any) => ({
        ...(prev || {}),
        company_name: trimmedName,
        company_country: trimmedCountry,
        company_phone: trimmedPhone,
        supplier_name: supplierContactName,
        supplier_country: supplierContactCountry,
        supplier_phone: supplierContactPhone,
        additional_country: supplierAdditionalCountry,
        additional_phone: supplierAdditionalPhone,
        active: supplierStatus?.toLowerCase() === "active",
        banner: supplierBanner,
      }));

      setSuppliers((prev) =>
        prev.map((s) =>
          String(s.id) === String(selectedSupplierId)
            ? {
                ...s,
                fullName: trimmedName,
                email: supplierContactName,
                phone: trimmedPhone,
                country: trimmedCountry,
                status: supplierStatus,
              }
            : s
        )
      );

      setIsEditingSupplier(false);
      setSupplierBannerFile(null);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error_description ||
        err?.message ||
        "Failed to update supplier.";
      toast.error(message);
    } finally {
      setSavingSupplier(false);
    }
  };

  const supplierFields: Field[] = [
    { name: "company_name", label: "Company Name" },
    {
      name: "company_country",
      label: "Company Country",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
    },
    { name: "company_phone", label: "Company Phone" },
    {
      name: "additional_country",
      label: "Additional Country",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
    },
    { name: "additional_phone", label: "Additional Phone" },
    { name: "supplier_name", label: "Supplier Name" },
    {
      name: "supplier_country",
      label: "Supplier Country",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
    },
    { name: "supplier_phone", label: "Supplier Phone" },
    {
      name: "active",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
    },
  ];

  
  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Purchases"
        subtitle="Suppliers and what you've bought from them."
      />
      <Box
        sx={{
          bgcolor: COLORS.WhiteBackground,
          borderRadius: 2,
          boxShadow: 3,
          position: "relative",
          minHeight: "80vh",
        }}
      >
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab
                label="Suppliers"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />

              <Tab
                label="View purchases"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="Supplier Details"
                disabled={!selectedSupplierId}
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="Supplier History"
                disabled={!selectedSupplierId}
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
            </Tabs>
          </>
        )}
        <Box p={2} ref={tabPanelRef}>
          {tab === 0 ? (
            <>
              <SearchHeaderContainer
                search={search}
                setSearch={setSearch}
                buttonConfig={[
                  {
                    onClick: () => setAddOpen(true),
                    label: "Add Supplier",
                    variant: "contained",
                    bgColor: COLORS.MainColor,
                    textColor: "#fff",
                    minWidth: 120,
                    minHeight: 36,
                    borderRadius: "10px",
                  },
                ]}
              />

              {suppliersError && !loadingSuppliers && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {suppliersError}
                </Alert>
              )}

              {loadingSuppliers ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress />
                </Box>
              ) : filteredSuppliers.length === 0 ? (
                <CustomLabel text="No suppliers found." />
              ) : (
                filteredSuppliers.map((supplier) => (
                  <Box
                    key={supplier.id}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 2,
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        src={
                          supplier.banner && String(supplier.banner).trim()
                            ? supplier.banner
                            : DEFAULT_IMAGE
                        }
                        variant="circular"
                        alt="Supplier Avatar"
                        sx={{ width: 80, height: 80 }}
                      />
                      <Box sx={{ ml: 2 }}>
                        <CustomLabel
                          text={supplier.fullName || "Supplier"}
                          fontWeight={700}
                          fontSize={16}
                        />
                        <CustomLabel
                          text={
                            supplier.companyPhone
                              ? `Company Phone: ${supplier.companyPhone}`
                              : "-"
                          }
                          fontSize={13}
                          color="#6b7280"
                        />
                        <CustomLabel
                          text={
                            supplier.supplierName
                              ? `Supplier: ${supplier.supplierName}${
                                  supplier.supplierPhone
                                    ? ` (${supplier.supplierPhone})`
                                    : ""
                                }`
                              : "-"
                          }
                          fontSize={12}
                          color="#6b7280"
                        />
                        <CustomLabel
                          text={
                            supplier.additionalPhone
                              ? `Additional: ${supplier.additionalPhone}`
                              : ""
                          }
                          fontSize={12}
                          color="#6b7280"
                        />
                        <Box
                          sx={{
                            mt: 1.5,
                            px: 1.25,
                            py: 0.35,
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#fff",
                            backgroundColor:
                              supplier.status === "Active"
                                ? "#22c55e"
                                : "#ef4444",
                            width: "fit-content",
                          }}
                        >
                          {supplier.status || "-"}
                        </Box>
                      </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                      <CustomButton
                        label="View supplier details"
                        onClick={() => {
                          setSelectedSupplierId(String(supplier.id));
                          setTab(2);
                          loadSupplierDetails(String(supplier.id));
                        }}
                        bgColor={COLORS.MainColor}
                        textColor={COLORS.WhiteText}
                        minWidth={190}
                        minHeight={34}
                        size="small"
                        borderRadius={10}
                      />
                      <CustomButton
                        label="View purchases history"
                        onClick={() => {
                          setSelectedSupplierId(String(supplier.id));
                          setTab(3);
                          loadSupplierDetails(String(supplier.id));
                        }}
                        bgColor="transparent"
                        textColor={COLORS.MainColor}
                        borderColor={COLORS.MainColor}
                        minWidth={190}
                        minHeight={34}
                        size="small"
                        borderRadius={10}
                        sx={{ border: `1px solid ${COLORS.MainColor}` }}
                      />
                    </Box>
                  </Box>
                ))
              )}
            </>
          ) : tab === 2 ? (
            <Box display="flex" gap={3}>
              <Box flex={2}>
                {supplierDetailsLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress />
                  </Box>
                ) : supplierDetailsError ? (
                  <CustomLabel text={supplierDetailsError} color={COLORS.RED} />
                ) : !supplierDetails ? (
                  <CustomLabel text="Select a supplier first." />
                ) : (
                  <>
                    <CustomLabel
                      text="Company Name"
                      variant="subtitle1"
                      gutterBottom
                    />
                    <CustomTextField
                      fullWidth
                      value={supplierName}
                      disabled={!isEditingSupplier}
                      onChange={(e) => setSupplierName(e.target.value)}
                    />

                    <CustomLabel
                      mt={2}
                      text="Supplier Name"
                      variant="subtitle1"
                      gutterBottom
                    />
                    <CustomTextField
                      fullWidth
                      value={supplierContactName}
                      disabled={!isEditingSupplier}
                      onChange={(e) => setSupplierContactName(e.target.value)}
                    />

                    <Box mt={2} display="flex" gap={2} alignItems="flex-end">
                      <Box minWidth={0} flex={0.2}>
                        <CustomLabel
                          text="Company Country"
                          variant="subtitle1"
                          gutterBottom
                        />
                        <CustomSelect
                          label="Company Country"
                          value={supplierCountry}
                          options={countries.map((c) => ({
                            label: `+${c.phone} - ${c.label}`,
                            value: `+${c.phone}`,
                          }))}
                          disabled={!isEditingSupplier}
                          onChange={(v: any) => setSupplierCountry(String(v))}
                          fullWidth
                          searchable
                        />
                      </Box>
                      <Box flex={0.8}>
                        <CustomLabel
                          text="Company Phone"
                          variant="subtitle1"
                          gutterBottom
                        />
                        <CustomTextField
                          fullWidth
                          value={supplierPhone}
                          disabled={!isEditingSupplier}
                          onChange={(e) => setSupplierPhone(e.target.value)}
                        />
                      </Box>
                    </Box>

                      <Box mt={2} display="flex" gap={2} alignItems="flex-end">
                        <Box minWidth={0} flex={0.2}>
                          <CustomLabel
                            text="Supplier Country"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomSelect
                            label="Supplier Country"
                            value={supplierContactCountry}
                            options={countries.map((c) => ({
                              label: `+${c.phone} - ${c.label}`,
                              value: `+${c.phone}`,
                            }))}
                            disabled={!isEditingSupplier}
                            onChange={(v: any) => setSupplierContactCountry(String(v))}
                            fullWidth
                            searchable
                          />
                        </Box>
                        <Box flex={0.8}>
                          <CustomLabel
                            text="Supplier Phone"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomTextField
                            fullWidth
                            value={supplierContactPhone}
                            disabled={!isEditingSupplier}
                            onChange={(e) => setSupplierContactPhone(e.target.value)}
                          />
                        </Box>
                      </Box>

                      <Box mt={2} display="flex" gap={2} alignItems="flex-end">
                        <Box minWidth={0} flex={0.2}>
                          <CustomLabel
                            text="Additional Country"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomSelect
                            label="Additional Country"
                            value={supplierAdditionalCountry}
                            options={countries.map((c) => ({
                              label: `+${c.phone} - ${c.label}`,
                              value: `+${c.phone}`,
                            }))}
                            disabled={!isEditingSupplier}
                            onChange={(v: any) =>
                              setSupplierAdditionalCountry(String(v))
                            }
                            fullWidth
                            searchable
                          />
                        </Box>
                        <Box flex={0.8}>
                          <CustomLabel
                            text="Additional Phone"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomTextField
                            fullWidth
                            value={supplierAdditionalPhone}
                            disabled={!isEditingSupplier}
                            onChange={(e) => setSupplierAdditionalPhone(e.target.value)}
                          />
                        </Box>
                      </Box>

                    <CustomLabel
                      mt={2}
                      text="Status"
                      variant="subtitle1"
                      gutterBottom
                    />
                    <CustomTextField
                      fullWidth
                      value={supplierStatus}
                      disabled={!isEditingSupplier}
                      select
                      onChange={(e) => setSupplierStatus(e.target.value as string)}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </CustomTextField>

                    <Box mt={10} display="flex" gap={2} alignItems="center" justifyContent="center">
                      <CustomButton
                        label="Edit"
                        textColor={COLORS.WhiteText}
                        bgColor={isEditingSupplier ? COLORS.BorderColor : COLORS.MainColor}
                        onClick={() => setIsEditingSupplier(true)}
                        disabled={isEditingSupplier || savingSupplier}
                      />
                      <CustomButton
                        label="Save"
                        textColor={COLORS.WhiteText}
                        bgColor={isEditingSupplier ? COLORS.MainColor : COLORS.BorderColor}
                        onClick={handleSaveSupplier}
                        disabled={
                          !isEditingSupplier ||
                          savingSupplier
                        }
                        loading={savingSupplier}
                      />
                    </Box>
                  </>
                )}
              </Box>
              <Box
                flex={1}
                borderLeft={1}
                borderColor="divider"
                pl={3}
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Avatar
                  src={
                    supplierBanner && String(supplierBanner).trim()
                      ? supplierBanner
                      : DEFAULT_IMAGE
                  }
                  variant="circular"
                  alt="Supplier Banner"
                  sx={{
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    objectFit: "cover",
                    cursor: isEditingSupplier ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (isEditingSupplier) {
                      supplierBannerInputRef.current?.click();
                    }
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={supplierBannerInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSupplierBannerFile(file);
                    }
                  }}
                />
                <Box
                  justifyContent="flex-start"
                  mt={5}
                  alignSelf="stretch"
                  width="100%"
                  display="flex"
                  flexDirection="column"
                >
                  <CustomLabel
                    text="Full Name"
                    variant="subtitle1"
                    gutterBottom={false}
                  />
                  <CustomLabel
                    text={
                      supplierName || supplierContactName || "-"
                    }
                    variant="body1"
                  />

                  <CustomLabel
                    mt={2}
                    text="Mobile Number"
                    variant="subtitle1"
                    gutterBottom={false}
                  />
                  <CustomLabel
                    text={
                      supplierPhone
                        ? `${supplierCountry ? `+${supplierCountry} ` : ""}${supplierPhone}`
                        : supplierContactPhone
                          ? `${supplierContactCountry ? `+${supplierContactCountry} ` : ""}${supplierContactPhone}`
                          : "-"
                    }
                    variant="body1"
                  />

                </Box>
              </Box>
            </Box>
          ) : tab === 3 ? (
            <Box display="flex" gap={3}>
              <Box flex={2}>
                <Box
                  mb={1}
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  alignItems="flex-end"
                >
                  <Box minWidth={90}>
                    <CustomLabel text="From" variant="body1" gutterBottom />
                    <CustomDateField
                      sx={{ width: "120px" }}
                      value={supplierHistoryFrom || ""}
                      onChange={(value) => {
                        setSupplierHistoryFrom(value || null);
                        setSupplierHistoryPage(1);
                      }}
                      fullWidth
                    />
                  </Box>

                  <Box minWidth={90}>
                    <CustomLabel text="To" variant="body1" gutterBottom />
                    <CustomDateField
                      sx={{ width: "120px" }}
                      value={supplierHistoryTo || ""}
                      onChange={(value) => {
                        setSupplierHistoryTo(value || null);
                        setSupplierHistoryPage(1);
                      }}
                      fullWidth
                    />
                  </Box>

                  <Box display="flex" alignItems="flex-end" gap={1} mb={0.5}>
                    <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af", mb: "6px" }}>
                      Rows per page:
                    </Typography>
                    <Select
                      value={supplierHistorySize}
                      onChange={(e) => {
                        setSupplierHistorySize(Number(e.target.value));
                        setSupplierHistoryPage(1);
                      }}
                      sx={{ height: "32px", fontSize: "0.875rem", width: "80px" }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </Box>

                  <Box display="flex" alignItems="flex-end" gap={0.5} mb={0.5}>
                    <IconButton
                      disabled={supplierHistoryPage <= 1}
                      onClick={() =>
                        setSupplierHistoryPage((p) => Math.max(p - 1, 1))
                      }
                      sx={{
                        color: supplierHistoryPage > 1 ? "#9ca3af" : "#ccc",
                        p: 0.5,
                      }}
                    >
                      <ArrowBackIosNew fontSize="small" />
                    </IconButton>

                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#9ca3af",
                        alignSelf: "center",
                      }}
                    >
                      {supplierHistoryPage} / {Math.max(1, Math.ceil(supplierHistoryTotal / supplierHistorySize))}
                    </Typography>

                    <IconButton
                      disabled={
                        supplierHistoryPage >=
                        Math.ceil(supplierHistoryTotal / supplierHistorySize)
                      }
                      onClick={() =>
                        setSupplierHistoryPage((p) =>
                          Math.min(
                            p + 1,
                            Math.ceil(supplierHistoryTotal / supplierHistorySize)
                          )
                        )
                      }
                      sx={{
                        color:
                          supplierHistoryPage <
                          Math.ceil(supplierHistoryTotal / supplierHistorySize)
                            ? "#9ca3af"
                            : "#ccc",
                        p: 0.5,
                      }}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                      Total Purchases: {supplierHistoryTotal}
                    </Typography>
                  </Box>
                </Box>

                {supplierHistoryLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress />
                  </Box>
                ) : supplierHistoryError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {supplierHistoryError}
                  </Alert>
                ) : supplierHistoryExpenses.length === 0 ? (
                  <CustomLabel text="No purchases found." />
                ) : (
                  supplierHistoryExpenses.map((exp, idx) => {
                    const dateRaw =
                      exp?.createdAt ||
                      exp?.created_at ||
                      exp?.updatedAt ||
                      exp?.updated_at;
                    const dateLabel = dateRaw
                      ? new Date(dateRaw).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—";

                    const amountValue = Number(exp?.amount) || 0;
                    const amountText = `${amountValue.toFixed(2)} ${exp?.currency || ""}`.trim();

                    const isPaid =
                      exp?.paid === true ||
                      String(exp?.status || "").toUpperCase() === "PAID";
                    const paymentStatus = isPaid ? "PAID" : "UNPAID";
                    const paymentStatusColor = isPaid ? "#16a34a" : "#ef4444";

                    return (
                      <Box
                        key={exp?.id || idx}
                        sx={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 2,
                          p: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                          mb: 1.5,
                        }}
                      >
                        <Box>
                          <CustomLabel
                            text={`${exp?.title || "Purchase"}${
                              exp?.metadata?.invoiceNumber || exp?.metadata?.invoice_nb
                                ? ` • #${exp?.metadata?.invoiceNumber || exp?.metadata?.invoice_nb}`
                                : ""
                            }`}
                            fontWeight={700}
                            fontSize={16}
                          />
                          <CustomLabel
                            text={exp?.description || "—"}
                            fontSize={14}
                            color="#4b5563"
                          />
                          <CustomLabel
                            text={exp?.notes ? `Notes: ${exp.notes}` : "Notes: —"}
                            fontSize={12}
                            color="#6b7280"
                          />
                          <CustomLabel
                            text={`Date: ${dateLabel}`}
                            fontSize={12}
                            color="#6b7280"
                          />
                          <CustomLabel
                            text={`Amount: ${amountText}`}
                            fontSize={12}
                            color="#6b7280"
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#fff",
                                backgroundColor: paymentStatusColor,
                                textAlign: "center",
                                minWidth: 90,
                              }}
                            >
                              {paymentStatus}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => openExpenseView(exp, selectedSupplierId)}
                              sx={{ color: COLORS.MainColor }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
              <Box
                flex={1}
                borderLeft={1}
                borderColor="divider"
                pl={3}
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                {!supplierDetails ? (
                  <CustomLabel text="Select a supplier first." />
                ) : (
                  <Box display="flex" flexDirection="column" gap={2} width="100%">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.75}>
                      <Avatar
                        src={
                          supplierDetails?.banner && String(supplierDetails.banner).trim()
                            ? supplierDetails.banner
                            : DEFAULT_IMAGE
                        }
                        variant="circular"
                        alt="Supplier Avatar"
                        sx={{ width: 140, height: 140 }}
                      />
                      <CustomLabel
                        text={supplierName || "Supplier"}
                        fontWeight={700}
                        fontSize={15}
                        center
                      />
                      <CustomLabel
                        text={supplierPhone || "-"}
                        fontSize={12}
                        color="#6b7280"
                        center
                      />
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Total Purchases"
                        fontWeight={600}
                        fontSize="0.9rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {supplierHistoryTotal}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Total Amount"
                        fontWeight={600}
                        fontSize="0.9rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {Number(supplierHistoryTotalAmount || 0).toFixed(2)} USD
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Unpaid"
                        fontWeight={600}
                        fontSize="1rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {supplierHistoryUnpaidSummary.amount.toFixed(2)} USD
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <>
              <Box display="flex" gap={3}>
                <Box flex={2}>
                  <Box
                    mb={1}
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    alignItems="flex-end"
                  >
                    <Box minWidth={90}>
                      <CustomLabel text="From" variant="body1" gutterBottom />
                      <CustomDateField
                        sx={{ width: "120px" }}
                        value={purchasesFrom || ""}
                        onChange={(value) => {
                          setPurchasesFrom(value || null);
                          setPurchasesPage(1);
                        }}
                        fullWidth
                      />
                    </Box>

                    <Box minWidth={90}>
                      <CustomLabel text="To" variant="body1" gutterBottom />
                      <CustomDateField
                        sx={{ width: "120px" }}
                        value={purchasesTo || ""}
                        onChange={(value) => {
                          setPurchasesTo(value || null);
                          setPurchasesPage(1);
                        }}
                        fullWidth
                      />
                    </Box>

                    <Box display="flex" alignItems="flex-end" gap={1} mb={0.5}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af", mb: "6px" }}>
                        Rows per page:
                      </Typography>
                      <Select
                        value={purchasesSize}
                        onChange={(e) => {
                          setPurchasesSize(Number(e.target.value));
                          setPurchasesPage(1);
                        }}
                        sx={{ height: "32px", fontSize: "0.875rem", width: "80px" }}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </Box>

                    <Box display="flex" alignItems="flex-end" gap={0.5} mb={0.5}>
                      <IconButton
                        disabled={purchasesPage <= 1}
                        onClick={() =>
                          setPurchasesPage((p) => Math.max(p - 1, 1))
                        }
                        sx={{ color: purchasesPage > 1 ? "#9ca3af" : "#ccc", p: 0.5 }}
                      >
                        <ArrowBackIosNew fontSize="small" />
                      </IconButton>

                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "#9ca3af",
                          alignSelf: "center",
                        }}
                      >
                        {purchasesPage} / {Math.max(1, Math.ceil(purchasesTotal / purchasesSize))}
                      </Typography>

                      <IconButton
                        disabled={purchasesPage >= Math.ceil(purchasesTotal / purchasesSize)}
                        onClick={() =>
                          setPurchasesPage((p) =>
                            Math.min(p + 1, Math.ceil(purchasesTotal / purchasesSize))
                          )
                        }
                        sx={{
                          color:
                            purchasesPage < Math.ceil(purchasesTotal / purchasesSize)
                              ? "#9ca3af"
                              : "#ccc",
                          p: 0.5,
                        }}
                      >
                        <ArrowForwardIos fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                        Total Purchases: {purchasesTotal}
                      </Typography>
                    </Box>
                  </Box>

                  {purchasesLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress />
                    </Box>
                  ) : purchasesError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {purchasesError}
                    </Alert>
                  ) : purchasesExpenses.length === 0 ? (
                    <CustomLabel text="No purchases found." />
                  ) : (
                    purchasesExpenses.map((exp, idx) => {
                      const dateRaw =
                        exp?.createdAt ||
                        exp?.created_at ||
                        exp?.updatedAt ||
                        exp?.updated_at;
                      const dateLabel = dateRaw
                        ? new Date(dateRaw).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—";

                      const amountValue = Number(exp?.amount) || 0;
                      const amountText = `${amountValue.toFixed(2)} ${exp?.currency || ""}`.trim();

                      const isPaid =
                        exp?.paid === true ||
                        String(exp?.status || "").toUpperCase() === "PAID";
                      const paymentStatus = isPaid ? "PAID" : "UNPAID";
                      const paymentStatusColor = isPaid ? "#16a34a" : "#ef4444";

                      return (
                        <Box
                          key={exp?.id || idx}
                          sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            mb: 1.5,
                          }}
                        >
                          <Box>
                          <CustomLabel
                            text={`${exp?.title || "Purchase"}${
                              exp?.metadata?.invoiceNumber || exp?.metadata?.invoice_nb
                                ? ` • #${exp?.metadata?.invoiceNumber || exp?.metadata?.invoice_nb}`
                                : ""
                            }`}
                            fontWeight={700}
                            fontSize={16}
                          />
                            <CustomLabel
                              text={exp?.description || "—"}
                              fontSize={14}
                              color="#4b5563"
                            />
                            <CustomLabel
                              text={exp?.notes ? `Notes: ${exp.notes}` : "Notes: —"}
                              fontSize={12}
                              color="#6b7280"
                            />
                            <CustomLabel
                              text={`Date: ${dateLabel}`}
                              fontSize={12}
                              color="#6b7280"
                            />
                            <CustomLabel
                              text={`Amount: ${amountText}`}
                              fontSize={12}
                              color="#6b7280"
                            />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#fff",
                                backgroundColor: paymentStatusColor,
                                textAlign: "center",
                                minWidth: 90,
                              }}
                            >
                              {paymentStatus}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => openExpenseView(exp)}
                              sx={{ color: COLORS.MainColor }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
                <Box
                  flex={1}
                  borderLeft={1}
                  borderColor="divider"
                  pl={3}
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Box display="flex" flexDirection="column" gap={1.5} width="100%">
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Total Purchases"
                        fontWeight={600}
                        fontSize="0.9rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {purchasesTotal}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Total Amount"
                        fontWeight={600}
                        fontSize="0.9rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {Number(purchasesTotalAmount || 0).toFixed(2)} USD
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Unpaid"
                        fontWeight={600}
                        fontSize="1rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {purchasesUnpaidSummary.amount.toFixed(2)} USD
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Average Purchase"
                        fontWeight={600}
                        fontSize="0.9rem"
                        center
                      />
                      <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                        {purchasesTotal > 0
                          ? (purchasesTotalAmount / purchasesTotal).toFixed(2)
                          : "0.00"}{" "}
                        USD
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                        width: "92%",
                        textAlign: "center",
                        minHeight: 90,
                      }}
                    >
                      <CustomLabel
                        text="Date Range"
                        fontWeight={600}
                        fontSize="0.9rem"
                        center
                      />
                      <Box sx={{ fontWeight: 600, fontSize: "0.95rem", mt: 0.5, color: "#6b7280" }}>
                        {formatDateDMY(purchasesFrom)} to {formatDateDMY(purchasesTo)}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <GenericFormDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Supplier"
        fields={supplierFields}
        disableSubmitUntilValid
        onSubmit={async (values) => {
          try {
            await createSupplier({
              company_name: values.company_name || null,
              company_country: values.company_country || null,
              company_phone: values.company_phone || null,
              additional_country: values.additional_country || null,
              additional_phone: values.additional_phone || null,
              supplier_name: values.supplier_name || null,
              supplier_country: values.supplier_country || null,
              supplier_phone: values.supplier_phone || null,
              active: values.active,
            });
            toast.success("Supplier created successfully!");
            setAddOpen(false);
            loadSuppliers();
          } catch (err: any) {
            const msg =
              err?.response?.data?.error_description ||
              err?.response?.data?.error ||
              err?.message ||
              "Failed to create supplier.";
            toast.error(msg);
          }
        }}
      />

      <CustomDialog
        open={viewExpenseOpen}
        onClose={() => {
          setViewExpenseOpen(false);
          setViewExpenseData(null);
          setViewExpenseError(null);
        }}
        title="Purchase Details"
        paperSx={{ minWidth: { sm: 760 }, maxWidth: 980 }}
      >
        {viewExpenseLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        ) : viewExpenseError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {viewExpenseError}
          </Alert>
        ) : !viewExpenseData ? (
          <CustomLabel text="No details available." />
        ) : (
          <ViewPurchaseAndPrint
            expense={viewExpenseData?.expense}
            items={viewExpenseData?.items}
          />
        )}
      </CustomDialog>
    </Box>
  );
};

export default Purchases;
